import { type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";
import moment from "moment-timezone";

import type { IncidentReport } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../hooks/useThrottledSectionUpdate";
import { ChooseYesNoOther } from "components/CriticalIncident/components/ChooseYesNoOther";
import { TextBox, DateTimeInput } from "components/Shared/Inputs";
import { FormControlWrapper } from "components/Shared";

import "./OnsiteContact.scss";

const MAX_CHARS = 2000;
const REFERRER_FIELDS_MAX_CHARS = 1000;

export const OnsiteContact: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('onsiteContact', values.onsiteContact);

  const updateSelection = (field: string, value: string) => {
    // Resetting initialContactDate because contactRemindedOfCiomAndSurvey is set to 'O'
    if (field === 'onsiteContact.contactRemindedOfCiomAndSurvey' && value === 'O') {
      setFieldValue('onsiteContact.initialContactDate', null);
    }
    setFieldValue(field, value);
    const fieldToUpdate = field.split('.').slice(1).join('.');
    throttledUpdateReport({ [fieldToUpdate]: value });
  };

  const makeChangeHandler = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(event);
    const fieldToUpdate = fieldName.split('.').slice(1).join('.');
    throttledUpdateReport({ [fieldToUpdate]: event.target.value });
  };

  return translationsReady && (
    <div className="onsite-contact">
      <h2>{t('onsiteContact')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('were_you_requested_to_leave_early')}
            name="onsiteContact.wereYouRequestedToLeaveEarly"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={t('were_you_requested_to_leave_early')}
              name="onsiteContact.wereYouRequestedToLeaveEarly"
              required
              value={values.onsiteContact?.wereYouRequestedToLeaveEarly}
              onChange={(value) => updateSelection('onsiteContact.wereYouRequestedToLeaveEarly', value)}
              error={touched.onsiteContact?.wereYouRequestedToLeaveEarly && Boolean(errors?.onsiteContact?.wereYouRequestedToLeaveEarly)}
              helperText={touched.onsiteContact?.wereYouRequestedToLeaveEarly ? t(errors?.onsiteContact?.wereYouRequestedToLeaveEarly || ' ') : ''}
            />
          </FormControlWrapper>
          {['Y', 'O'].includes(values.onsiteContact?.wereYouRequestedToLeaveEarly) && (
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('authorizer_name_and_comments')}
              name="onsiteContact.wereYouRequestedToLeaveEarlyOtherReason"
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={t('authorizer_name_and_comments')}
                required
                name="onsiteContact.wereYouRequestedToLeaveEarlyOtherReason"
                value={values.onsiteContact?.wereYouRequestedToLeaveEarlyOtherReason}
                onChange={makeChangeHandler('onsiteContact.wereYouRequestedToLeaveEarlyOtherReason')}
                slotProps={{ input: { maxLength: MAX_CHARS } }}
                error={touched.onsiteContact?.wereYouRequestedToLeaveEarlyOtherReason && Boolean(errors?.onsiteContact?.wereYouRequestedToLeaveEarlyOtherReason)}
                helperText={touched.onsiteContact?.wereYouRequestedToLeaveEarlyOtherReason ? t(errors?.onsiteContact?.wereYouRequestedToLeaveEarlyOtherReason || ' ') : ''}
              />
            </FormControlWrapper>
          )}
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('was_organization_contact_supportive')}
            name="onsiteContact.wasOrganizationContactSupportive"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={t('was_organization_contact_supportive')}
              name="onsiteContact.wasOrganizationContactSupportive"
              required
              value={values.onsiteContact?.wasOrganizationContactSupportive}
              onChange={(value) => updateSelection('onsiteContact.wasOrganizationContactSupportive', value)}
              error={touched.onsiteContact?.wasOrganizationContactSupportive && Boolean(errors?.onsiteContact?.wasOrganizationContactSupportive)}
              helperText={touched.onsiteContact?.wasOrganizationContactSupportive ? t(errors?.onsiteContact?.wasOrganizationContactSupportive || ' ') : ''}
            />
          </FormControlWrapper>
          {['N', 'O'].includes(values.onsiteContact?.wasOrganizationContactSupportive) && (
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('organization_contact_supportive_description')}
              name="onsiteContact.wasOrganizationContactSupportiveOtherReason"
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={t('organization_contact_supportive_description')}
                required
                name="onsiteContact.wasOrganizationContactSupportiveOtherReason"
                value={values.onsiteContact?.wasOrganizationContactSupportiveOtherReason}
                onChange={makeChangeHandler('onsiteContact.wasOrganizationContactSupportiveOtherReason')}
                slotProps={{ input: { maxLength: MAX_CHARS } }}
                error={touched.onsiteContact?.wasOrganizationContactSupportiveOtherReason && Boolean(errors?.onsiteContact?.wasOrganizationContactSupportiveOtherReason)}
                helperText={touched.onsiteContact?.wasOrganizationContactSupportiveOtherReason ? t(errors?.onsiteContact?.wasOrganizationContactSupportiveOtherReason || ' ') : ''}
              />
            </FormControlWrapper>
          )}
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('were_there_barriers_to_intervention')}
            name="onsiteContact.wereThereBarriersToIntervention"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={t('were_there_barriers_to_intervention')}
              name="onsiteContact.wereThereBarriersToIntervention"
              required
              value={values.onsiteContact?.wereThereBarriersToIntervention}
              onChange={(value) => updateSelection('onsiteContact.wereThereBarriersToIntervention', value)}
              error={touched.onsiteContact?.wereThereBarriersToIntervention && Boolean(errors?.onsiteContact?.wereThereBarriersToIntervention)}
              helperText={touched.onsiteContact?.wereThereBarriersToIntervention ? t(errors?.onsiteContact?.wereThereBarriersToIntervention || ' ') : ''}
            />
          </FormControlWrapper>
          {['Y', 'O'].includes(values.onsiteContact?.wereThereBarriersToIntervention) && (
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('please_specify')}
              name="onsiteContact.wereThereBarriersToInterventionOtherReason"
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={t('please_specify')}
                required
                name="onsiteContact.wereThereBarriersToInterventionOtherReason"
                value={values.onsiteContact?.wereThereBarriersToInterventionOtherReason}
                onChange={makeChangeHandler('onsiteContact.wereThereBarriersToInterventionOtherReason')}
                slotProps={{ input: { maxLength: MAX_CHARS } }}
                error={touched.onsiteContact?.wereThereBarriersToInterventionOtherReason && Boolean(errors?.onsiteContact?.wereThereBarriersToInterventionOtherReason)}
                helperText={touched.onsiteContact?.wereThereBarriersToInterventionOtherReason ? t(errors?.onsiteContact?.wereThereBarriersToInterventionOtherReason || ' ') : ''}
              />
            </FormControlWrapper>
          )}
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('organization_and_management_support_summary')}
            name="onsiteContact.organizationAndManagementSupportSummary"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('organization_and_management_support_summary')}
              name="onsiteContact.organizationAndManagementSupportSummary"
              value={values.onsiteContact?.organizationAndManagementSupportSummary}
              onChange={makeChangeHandler('onsiteContact.organizationAndManagementSupportSummary')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>

        <Grid item sm={6}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('referrer_title')}
            name="onsiteContact.referrerTitle"
            fullWidth
          >
            <TextBox
              label={t('referrer_title')}
              required
              name="onsiteContact.referrerTitle"
              value={values.onsiteContact?.referrerTitle}
              onChange={makeChangeHandler('onsiteContact.referrerTitle')}
              slotProps={{ input: { maxLength: REFERRER_FIELDS_MAX_CHARS } }}
              error={touched.onsiteContact?.referrerTitle && Boolean(errors?.onsiteContact?.referrerTitle)}
              helperText={touched.onsiteContact?.referrerTitle ? t(errors?.onsiteContact?.referrerTitle || ' ') : ''}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={6}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('referrer_name')}
            name="onsiteContact.referrerName"
            fullWidth
          >
            <TextBox
              label={t('referrer_name')}
              required
              name="onsiteContact.referrerName"
              value={values.onsiteContact?.referrerName}
              onChange={makeChangeHandler('onsiteContact.referrerName')}
              slotProps={{ input: { maxLength: REFERRER_FIELDS_MAX_CHARS } }}
              error={touched.onsiteContact?.referrerName && Boolean(errors?.onsiteContact?.referrerName)}
              helperText={touched.onsiteContact?.referrerName ? t(errors?.onsiteContact?.referrerName || ' ') : ''}
            />
          </FormControlWrapper>
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('contact_reminded_of_ciom_and_survey')}
            name="onsiteContact.contactRemindedOfCiomAndSurvey"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={t('contact_reminded_of_ciom_and_survey')}
              name="onsiteContact.contactRemindedOfCiomAndSurvey"
              value={values.onsiteContact?.contactRemindedOfCiomAndSurvey}
              onChange={(value) => updateSelection('onsiteContact.contactRemindedOfCiomAndSurvey', value)}
              error={touched.onsiteContact?.contactRemindedOfCiomAndSurvey && Boolean(errors?.onsiteContact?.contactRemindedOfCiomAndSurvey)}
              helperText={touched.onsiteContact?.contactRemindedOfCiomAndSurvey ? t(errors?.onsiteContact?.contactRemindedOfCiomAndSurvey || ' ') : ''}
            />
          </FormControlWrapper>
          {['N', 'O'].includes(values.onsiteContact?.contactRemindedOfCiomAndSurvey) && (
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('ciom_and_survey_description')}
              name="onsiteContact.contactRemindedOfCiomAndSurveyOtherReason"
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={t('ciom_and_survey_description')}
                required
                name="onsiteContact.contactRemindedOfCiomAndSurveyOtherReason"
                value={values.onsiteContact?.contactRemindedOfCiomAndSurveyOtherReason ?? ''}
                onChange={makeChangeHandler('onsiteContact.contactRemindedOfCiomAndSurveyOtherReason')}
                slotProps={{ input: { maxLength: MAX_CHARS } }}
                error={touched.onsiteContact?.contactRemindedOfCiomAndSurveyOtherReason && Boolean(errors?.onsiteContact?.contactRemindedOfCiomAndSurveyOtherReason)}
                helperText={touched.onsiteContact?.contactRemindedOfCiomAndSurveyOtherReason ? t(errors?.onsiteContact?.contactRemindedOfCiomAndSurveyOtherReason || ' ') : ''}
              />
            </FormControlWrapper>
          )}
          {['Y', 'N'].includes(values.onsiteContact?.contactRemindedOfCiomAndSurvey) && (
            <FormControlWrapper
              isReadonly={isReadonly}
              type="date"
              label={`${t('initial_contact_date_and_time')}*`}
              name="onsiteContact.initialContactDate"
              dateFormat="L LT"
            >
              <DateTimeInput
                label={`${t('initial_contact_date_and_time')}*`}
                name="onsiteContact.initialContactDate"
                onChange={(val) => {
                  const isoString = val?.toDate().toISOString();
                  setFieldValue('onsiteContact.initialContactDate', isoString);
                  throttledUpdateReport({ initialContactDate: isoString });
                }}
                value={values.onsiteContact?.initialContactDate ? moment(values.onsiteContact.initialContactDate) : null}
                className="initial_contact_date_wrapper"
                error={touched.onsiteContact?.initialContactDate && Boolean(errors.onsiteContact?.initialContactDate)}
                helperText={touched.onsiteContact?.initialContactDate ? t(errors.onsiteContact?.initialContactDate || ' ') : ''}
                fullWidth
              />
            </FormControlWrapper>
          )}
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('contact_information')}
            name="onsiteContact.contactInformation"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('contact_information')}
              required
              name="onsiteContact.contactInformation"
              value={values.onsiteContact?.contactInformation}
              onChange={makeChangeHandler('onsiteContact.contactInformation')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
              error={touched.onsiteContact?.contactInformation && Boolean(errors?.onsiteContact?.contactInformation)}
              helperText={touched.onsiteContact?.contactInformation ? t(errors?.onsiteContact?.contactInformation || ' ') : ''}
            />
          </FormControlWrapper>
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('onsite_contact_description')}
            name="onsiteContact.onsiteContactDescription"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('onsite_contact_description')}
              name="onsiteContact.onsiteContactDescription"
              value={values.onsiteContact?.onsiteContactDescription}
              onChange={makeChangeHandler('onsiteContact.onsiteContactDescription')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('agreed_actions')}
            name="onsiteContact.agreedActions"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('agreed_actions')}
              name="onsiteContact.agreedActions"
              value={values.onsiteContact?.agreedActions}
              onChange={makeChangeHandler('onsiteContact.agreedActions')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('follow_up_eap_services_available')}
            name="onsiteContact.followUpEapServicesAvailable"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('follow_up_eap_services_available')}
              name="onsiteContact.followUpEapServicesAvailable"
              value={values.onsiteContact?.followUpEapServicesAvailable}
              onChange={makeChangeHandler('onsiteContact.followUpEapServicesAvailable')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('overlaying_trends_or_themes')}
            name="onsiteContact.overlayingTrendsOrThemes"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('overlaying_trends_or_themes')}
              name="onsiteContact.overlayingTrendsOrThemes"
              value={values.onsiteContact?.overlayingTrendsOrThemes}
              onChange={makeChangeHandler('onsiteContact.overlayingTrendsOrThemes')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>
      </Grid>
    </div>
  );
};
